import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Edit, Send, Eye, EyeOff, Trash2, Plus, BarChart3, TrendingUp } from "lucide-react";
import ShareSheet from "../../components/ShareSheet";
import { CURRENCY_SYMBOLS } from "../../lib/currencies";

export default function AdminDealsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [stats, setStats] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterRegion, setFilterRegion] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [shareDealing, setShareDealing] = useState(null);

  const REGIONS = ["Europe", "Asia", "Americas", "Africa", "Middle East", "Oceania"];

  const load = () => {
    setLoading(true);
    setLoadError(null);
    const dealsUrl = api.defaults.baseURL + "/admin/deals";
    console.log("[AdminDealsList] fetching:", dealsUrl, "withCredentials:", api.defaults.withCredentials);
    Promise.all([
      api.get("/admin/deals")
        .then((r) => {
          console.log("[AdminDealsList] /admin/deals response:", r.status, "count:", r.data?.length);
          setDeals(r.data);
        })
        .catch((err) => {
          const status = err.response?.status;
          const detail = err.response?.data?.detail || err.message;
          console.error("[AdminDealsList] /admin/deals failed:", status, detail, err);
          if (status === 401) { navigate("/admin/login"); return; }
          setLoadError(`${status || "Network error"}: ${detail}`);
        }),
      api.get("/admin/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };

  // Trigger load when auth state is confirmed — handles new browser sessions
  // where /auth/me resolves async before this component first renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) load(); }, [user]);

  const toggle = async (d) => {
    await api.patch(`/admin/deals/${d.id}`, { published: !d.published });
    toast.success(d.published ? "FILE UNPUBLISHED" : "FILE PUBLISHED");
    load();
  };
  const remove = async (d) => {
    if (!window.confirm("Permanently delete this file?")) return;
    await api.delete(`/admin/deals/${d.id}`);
    toast.success("FILE DELETED");
    load();
  };
  const toggleExpired = async (d) => {
    await api.patch(`/admin/deals/${d.id}`, { expired: !d.expired });
    toast.success(d.expired ? "DEAL REINSTATED" : "DEAL MARKED EXPIRED");
    load();
  };
  const sendNewsletter = async (d) => {
    const label = d.deal_type === "hotel" ? (d.hotel_name || d.to_city)
      : d.deal_type === "cruise" ? (d.cruise_line || "Cruise")
      : `${d.from_city} → ${d.to_city}`;
    if (!window.confirm(`Dispatch newsletter for "${label}"?`)) return;
    try {
      const { data } = await api.post(`/admin/newsletter/send/${d.id}`);
      if (data.errors > 0 && data.sent === 0) {
        toast.error(`DISPATCH FAILED — ${data.errors} error${data.errors === 1 ? "" : "s"} (check Resend logs)`);
      } else if (data.errors > 0) {
        toast.success(`DISPATCHED TO ${data.sent} · ${data.errors} FAILED`);
      } else {
        const tag = data.provider === "mock" ? " (MOCK — no Resend key)" : "";
        toast.success(`DISPATCHED TO ${data.sent} OPERATORS${tag}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "unknown error";
      toast.error(`NEWSLETTER DISPATCH FAILED — ${detail}`);
    }
  };
  const sendTestNewsletter = async (d) => {
    try {
      const { data } = await api.post(`/admin/newsletter/send-test/${d.id}`);
      const tag = data.provider === "mock" ? " (MOCK — no Resend key)" : "";
      toast.success(`TEST DISPATCHED TO ${data.to}${tag}`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "unknown error";
      toast.error(`TEST DISPATCH FAILED — ${detail}`);
    }
  };
  const dealPageUrl = (d) => `/deal/${d.slug || d.id}`;
  const shareText = (d) => {
    const url = `${window.location.origin}/deal/${d.slug || d.id}`;
    const sym = CURRENCY_SYMBOLS[d.price_currency] || "€";
    const returnFrom = d.return_from_city || d.return_from_airport || "";
    const returnTo = d.return_to_city || d.return_to_airport || d.from_city || "";
    const retKey = returnFrom.toUpperCase().split("(")[0].trim();
    const toKey = (d.to_city || d.to_airport || "").toUpperCase().split("(")[0].trim();
    const isOpenJaw = returnFrom && retKey !== toKey;
    const route = isOpenJaw
      ? `${d.from_city} → ${d.to_city} / ${returnFrom} → ${returnTo}`
      : `${d.from_city} → ${d.to_city}`;
    return `▌ RESTRICTED DEAL — ${route} for ${sym}${d.price_eur} on ${d.airline}\nTravel: ${d.travel_window}\n${url}`;
  };
  const copyShare = async (d) => {
    await navigator.clipboard.writeText(shareText(d));
    toast.success("SHARE TEXT COPIED");
  };

  const btnBase = "font-mono text-[0.6rem] tracking-[0.2em] uppercase px-3 py-1.5 border transition-colors";
  const btnActive = `${btnBase} border-white text-white bg-[#1A1A1A]`;
  const btnIdle = `${btnBase} border-[#3A3A3A] text-[#9A8F83] hover:border-[#9A8F83] hover:text-white`;

  const dealStatus = (d) => {
    if (d.expired) return "expired";
    if (!d.travel_window) return "check";
    const text = d.travel_window.toUpperCase();
    const yearMatch = text.match(/\b(202\d|203\d)\b/);
    if (!yearMatch) return "check";
    const year = parseInt(yearMatch[1], 10);
    const MONTHS = { JAN:1,FEB:2,MAR:3,APR:4,MAY:5,JUN:6,JUL:7,AUG:8,SEP:9,OCT:10,NOV:11,DEC:12,
      JANUARY:1,FEBRUARY:2,MARCH:3,APRIL:4,JUNE:6,JULY:7,AUGUST:8,SEPTEMBER:9,OCTOBER:10,NOVEMBER:11,DECEMBER:12 };
    const months = Object.entries(MONTHS).filter(([k]) => text.includes(k)).map(([,v]) => v);
    if (!months.length) return "check";
    const endMonth = Math.max(...months);
    const lastDay = new Date(year, endMonth, 0).getDate();
    const endDate = new Date(Date.UTC(year, endMonth - 1, lastDay, 23, 59, 59));
    const now = Date.now();
    console.log('deal:', d.slug, 'travel_window:', d.travel_window, 'endDate:', endDate, 'now:', Date.now(), 'diff days:', (endDate - Date.now()) / 86400000);
    if (endDate < now) return "expired";
    if (endDate - now < 7 * 86400 * 1000) return "expiring";
    return "active";
  };

  const typeCounts = {
    all:     deals.length,
    flight:  deals.filter((d) => (d.deal_type || "flight") === "flight").length,
    hotel:   deals.filter((d) => d.deal_type === "hotel").length,
    cruise:  deals.filter((d) => d.deal_type === "cruise").length,
    package: deals.filter((d) => d.deal_type === "package").length,
  };
  const regionCounts = {
    all: deals.length,
    ...Object.fromEntries(REGIONS.map((r) => [r, deals.filter((d) => (d.tags || []).includes(r)).length])),
  };
  const statusCounts = {
    all:      deals.length,
    active:   deals.filter((d) => dealStatus(d) === "active").length,
    expiring: deals.filter((d) => dealStatus(d) === "expiring").length,
    expired:  deals.filter((d) => dealStatus(d) === "expired").length,
  };

  const filtered = deals.filter((d) => {
    const typeMatch = filterType === "all" || (d.deal_type || "flight") === filterType;
    const regionMatch = filterRegion === "all" || (d.tags || []).includes(filterRegion);
    const status = dealStatus(d);
    const statusMatch = filterStatus === "all"
      || (filterStatus === "active" && status === "active")
      || (filterStatus === "expiring" && status === "expiring")
      || (filterStatus === "expired" && status === "expired");
    return typeMatch && regionMatch && statusMatch;
  });

  return (
    <div className="max-w-6xl" data-testid="admin-deals-list">
      <header className="mb-10">
        <p className="eyebrow eyebrow-red">// INDEX</p>
        <div className="flex flex-col gap-3 mt-2 md:flex-row md:items-end md:justify-between">
          <h1 className="font-mono text-4xl md:text-5xl text-white uppercase tracking-tight">All files</h1>
          <Link to="/admin/new" className="btn-rd w-full md:w-auto justify-center" data-testid="new-deal-cta">
            <Plus className="w-4 h-4" /> NEW FILE
          </Link>
        </div>
      </header>

      {/* Dashboard strip: clicks this week + top 3 */}
      {stats && (
        <section className="mb-10 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6" data-testid="admin-dashboard">
          <div className="rd-card p-6">
            <p className="eyebrow eyebrow-red flex items-center gap-2">
              <TrendingUp className="w-3 h-3" /> // CLICKS
            </p>
            <p className="font-mono text-5xl text-white mt-3 tracking-tight" data-testid="stat-week-clicks">
              {stats.clicks_week}
            </p>
            <p className="mt-1 text-[11px] font-mono tracking-widest uppercase text-[#9A8F83]">
              LAST 7 DAYS
            </p>
            {stats.total_clicks != null && (
              <p className="mt-3 text-[11px] font-mono tracking-wider uppercase text-[#9A8F83] break-words">
                {stats.total_clicks} ALL TIME · {stats.published_deals} LIVE FILE{stats.published_deals === 1 ? "" : "S"}
              </p>
            )}
          </div>
          <div className="rd-card p-6">
            <p className="eyebrow eyebrow-red flex items-center gap-2">
              <BarChart3 className="w-3 h-3" /> // TOP 3 BY CLICKS
            </p>
            {stats.top_deals.length === 0 ? (
              <p className="mt-4 font-mono text-xs tracking-widest uppercase text-[#9A8F83]">
                NO CLICK DATA YET
              </p>
            ) : (
              <ol className="mt-3 space-y-2" data-testid="top-deals-list">
                {stats.top_deals.map((d, i) => (
                  <li key={d.id} className="flex items-center gap-3 font-mono text-sm">
                    <span className="text-[#CC1A1A] w-6 shrink-0">#{i + 1}</span>
                    <Link
                      to={`/admin/edit/${d.id}`}
                      className="flex-1 min-w-0 truncate text-white hover:text-[#CC1A1A] transition-colors uppercase tracking-tight"
                    >
                      {d.from_city} → {d.to_city}
                    </Link>
                    <span className="text-[#FFDD57] shrink-0">€{d.price_eur}</span>
                    <span className="rd-badge rd-badge-outline !text-[0.6rem] shrink-0">{d.click_count} CLICKS</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </section>
      )}

      {loading ? (
        <p className="text-xs font-mono text-[#9A8F83] tracking-widest uppercase">// loading…</p>
      ) : loadError ? (
        <div className="rd-card p-12 text-center" data-testid="admin-deals-error">
          <p className="eyebrow eyebrow-red">// FETCH ERROR</p>
          <h2 className="font-mono text-2xl mt-3 text-white uppercase">Failed to load files</h2>
          <p className="mt-3 text-sm font-mono text-[#CC1A1A] tracking-wider">{loadError}</p>
          <p className="mt-2 text-xs font-mono text-[#9A8F83] tracking-wider uppercase">
            Check: CORS_ORIGINS includes this domain · session cookie is present · backend is reachable
          </p>
          <button onClick={load} className="btn-rd mt-6 inline-flex">RETRY →</button>
        </div>
      ) : deals.length === 0 ? (
        <div className="rd-card p-12 text-center" data-testid="admin-deals-empty">
          <p className="eyebrow eyebrow-red">// EMPTY</p>
          <h2 className="font-mono text-3xl mt-3 text-white uppercase">No files yet</h2>
          <Link to="/admin/new" className="btn-rd mt-6 inline-flex">POST FIRST FILE →</Link>
        </div>
      ) : (
          <>
            {/* ── Filters ── */}
            <div className="flex flex-wrap gap-x-6 gap-y-3 mb-5">
              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[#555] mr-1">TYPE</span>
                {[["all","ALL"],["flight","FLIGHTS"],["hotel","HOTELS"],["cruise","CRUISES"],["package","PACKAGES"]].map(([v, label]) => (
                  <button key={v} onClick={() => setFilterType(v)} className={filterType === v ? btnActive : btnIdle}>
                    {label} <span style={{ color: "#666" }}>({typeCounts[v]})</span>
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[#555] mr-1">REGION</span>
                <button onClick={() => setFilterRegion("all")} className={filterRegion === "all" ? btnActive : btnIdle}>
                  ALL <span style={{ color: "#666" }}>({regionCounts.all})</span>
                </button>
                {REGIONS.map((r) => (
                  <button key={r} onClick={() => setFilterRegion(r)} className={filterRegion === r ? btnActive : btnIdle}>
                    {r.toUpperCase()} <span style={{ color: "#666" }}>({regionCounts[r]})</span>
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[#555] mr-1">STATUS</span>
                {[["all","ALL"],["active","ACTIVE"],["expiring","EXPIRING SOON"],["expired","EXPIRED"]].map(([v, label]) => (
                  <button key={v} onClick={() => setFilterStatus(v)} className={filterStatus === v ? btnActive : btnIdle}>
                    {label} <span style={{ color: "#666" }}>({statusCounts[v]})</span>
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="rd-card p-10 text-center">
                <p className="font-mono text-sm text-[#9A8F83] tracking-widest uppercase">// No files match these filters</p>
              </div>
            ) : (
              <div className="rd-card divide-y divide-[#2A2A2A]">
                {filtered.map((d) => (
                  <div
                    key={d.id}
                    className="p-4 md:p-6 grid md:grid-cols-[80px_1fr_auto] gap-4 md:gap-6 items-center"
                    data-testid={`admin-deal-row-${d.id}`}
                  >
                    <div className="w-20 h-20 bg-[#0E0E0E] border border-[#2A2A2A] overflow-hidden shrink-0">
                      {d.image_url && <img src={d.image_url} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`rd-badge ${d.published ? "" : "rd-badge-dim"}`}>
                          {d.published ? "LIVE" : "DRAFT"}
                        </span>
                        {(() => {
                          const s = dealStatus(d);
                          if (s === "expired") return <span className="rd-badge" style={{ background: "#CC1A1A", color: "#fff" }}>EXPIRED</span>;
                          if (s === "expiring") return <span className="rd-badge" style={{ background: "#C25A0A", color: "#fff" }}>EXPIRING SOON</span>;
                          if (s === "check") return <span className="rd-badge rd-badge-dim">CHECK</span>;
                          return null;
                        })()}
                        <span className="rd-badge rd-badge-outline">{d.tags?.[0] || "—"}</span>
                        <span className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#666]">
                          FILE.{d.id?.slice(0, 6).toUpperCase()}
                        </span>
                      </div>
                      <p className="font-mono text-xl md:text-2xl text-white mt-2 tracking-tight uppercase">
                        {d.deal_type === "hotel"
                          ? (d.hotel_name || d.to_city)
                          : d.deal_type === "cruise"
                          ? (d.departure_port && d.destination_port
                              ? <>{d.departure_port} <span className="text-[#CC1A1A]">→</span> {d.destination_port}</>
                              : d.cruise_line || d.to_city || "Cruise")
                          : <>{d.from_city || "?"} <span className="text-[#CC1A1A]">→</span> {d.to_city || "?"}</>}{" "}
                        <span className="text-[#FFDD57]">{CURRENCY_SYMBOLS[d.price_currency] || "€"}{d.price_eur}</span>
                      </p>
                      <p className="text-[11px] text-[#9A8F83] mt-1 font-mono tracking-wider uppercase">
                        {d.deal_type === "cruise"
                          ? (d.cruise_line || "Cruise")
                          : (d.airline || d.deal_type?.toUpperCase())}
                        {" · "}
                        {d.travel_window || d.checkin_period || d.cruise_route || ""}
                        {d.sent_count ? <span className="text-[#FFDD57] ml-2">· DISPATCHED TO {d.sent_count}</span> : null}
                        {d.click_count ? <span className="text-[#CC1A1A] ml-2">· {d.click_count} CLICK{d.click_count === 1 ? "" : "S"}</span> : null}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <button onClick={() => setShareDealing(d)} className="btn-rd-outline !py-2 !px-3 !text-[0.6rem]" data-testid={`share-copy-${d.id}`}>
                          📤 SHARE
                        </button>
                        <button onClick={() => sendNewsletter(d)} className="btn-rd-outline !py-2 !px-3 !text-[0.6rem]" data-testid={`send-newsletter-${d.id}`}>
                          <Send className="w-3 h-3" /> DISPATCH
                        </button>
                        <Link to={`/admin/edit/${d.id}`} className="btn-rd-outline !py-2 !px-3 !text-[0.6rem]" data-testid={`edit-${d.id}`}>
                          <Edit className="w-3 h-3" /> EDIT
                        </Link>
                        <button onClick={() => toggleExpired(d)} className={`btn-rd-outline !py-2 !px-3 !text-[0.6rem] ${d.expired ? "!border-[#9A8F83] !text-[#9A8F83]" : "!border-[#CC1A1A] !text-[#CC1A1A]"}`} data-testid={`expire-${d.id}`}>
                          {d.expired ? "↺ REINSTATE" : "⊘ EXPIRE"}
                        </button>
                        <button onClick={() => toggle(d)} className="btn-rd-outline !py-2 !px-3 !text-[0.6rem]" data-testid={`toggle-${d.id}`}>
                          {d.published ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          {d.published ? "UNPUBLISH" : "PUBLISH"}
                        </button>
                        <button onClick={() => remove(d)} className="btn-rd-outline !py-2 !px-3 !text-[0.6rem] hover:!border-[#CC1A1A] hover:!text-[#CC1A1A]" data-testid={`delete-${d.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button onClick={() => sendTestNewsletter(d)} className="btn-rd-outline !py-2 !px-3 !text-[0.6rem] !border-[#9A8F83] !text-[#9A8F83]" data-testid={`send-test-newsletter-${d.id}`}>
                          <Send className="w-3 h-3" /> TEST DISPATCH
                        </button>
                        <a href={dealPageUrl(d)} target="_blank" rel="noreferrer" className="btn-rd-outline !py-2 !px-3 !text-[0.6rem]" data-testid={`view-live-${d.id}`}>
                          <Eye className="w-3 h-3" /> VIEW LIVE
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
      )}

      {shareDealing && (
        <div className="fixed inset-0 z-50 bg-[#111111] overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 py-8">
            <ShareSheet
              deal={shareDealing}
              onBack={() => setShareDealing(null)}
              onTestDispatch={async () => {
                await sendTestNewsletter(shareDealing);
                setShareDealing(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
